/**
 * `documents.*` — the Document Management System (Phase 1).
 *
 * Two-phase upload (requestUpload → SPA PUT → create), foldered + tagged,
 * cursor-paginated list with title search + tag filter, presigned download,
 * soft-delete. Folder CRUD is a sibling surface. Mirrors `attachments.*`,
 * generalised to a first-class document entity.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/** MIME allow-list — keep in sync with `ALLOWED_DOCUMENT_TYPES` in domain. */
export const documentMime = z.enum([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
]);
export type DocumentMime = z.infer<typeof documentMime>;

/** 50 MB — keep in sync with `DOC_MAX_BYTES` in domain. */
const MAX_BYTES = 50 * 1024 * 1024;

const filename = z.string().trim().min(1, "Filename is required").max(255, "Filename is too long");
const tags = z.array(z.string().trim().min(1).max(40)).max(20).default([]);

// ── upload (two-phase) ────────────────────────────────────────────

export const requestUploadInput = z.object({
  originalName: filename,
  contentType: documentMime,
  sizeBytes: z.number().int().min(1, "File is empty").max(MAX_BYTES, `Max ${MAX_BYTES} bytes (~50 MB)`),
});
export const requestUploadOutput = z.object({
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  contentType: documentMime,
  maxBytes: z.number().int(),
});

export const createInput = z.object({
  objectKey: z.string().min(1),
  title: z.string().trim().max(255).default(""),
  originalName: filename,
  contentType: documentMime,
  sizeBytes: z.number().int().min(1).max(MAX_BYTES),
  folderId: uuid.nullable().default(null),
  tags,
});
export const createOutput = z.object({ id: uuid, storageKey: z.string() });

// ── list ──────────────────────────────────────────────────────────

export const documentKind = z.enum(["pdf", "image", "file"]);
export type DocumentKind = z.infer<typeof documentKind>;

const kbStatus = z.enum(["none", "pending", "indexed", "failed"]);

export const listInput = z.object({
  folderId: uuid.nullable().default(null),
  search: z.string().trim().max(120).optional(),
  tags: z.array(z.string().trim().min(1)).optional(),
  kind: documentKind.optional(),
  /** When true, return only documents marked as knowledge base (ignores folder). */
  kbOnly: z.boolean().optional(),
  cursor: z.string().nullable().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const summaryInput = z.object({});
export const summaryOutput = z.object({
  total: z.number().int(),
  /** Stringified bigint of summed bytes. */
  storageBytes: z.string(),
  addedLast30d: z.number().int(),
  folders: z.number().int(),
  byKind: z.object({ pdf: z.number().int(), image: z.number().int(), file: z.number().int() }),
});
export const listRow = z.object({
  id: uuid,
  folderId: uuid.nullable(),
  title: z.string(),
  originalName: z.string(),
  contentType: z.string(),
  /** Stringified bigint so JSON round-trips don't lose precision. */
  sizeBytes: z.string(),
  tags: z.array(z.string()),
  isKb: z.boolean(),
  kbStatus,
  uploadedAt: z.string().datetime(),
});
export const listOutput = z.object({
  items: z.array(listRow),
  nextCursor: z.string().nullable(),
});

// ── download / delete ─────────────────────────────────────────────

export const getDownloadUrlInput = z.object({ id: uuid });
export const getDownloadUrlOutput = z.object({ url: z.string().url(), originalName: z.string() });

export const deleteInput = z.object({ id: uuid });
export const deleteOutput = z.object({ id: uuid, deletedAt: z.string().datetime() });

// ── knowledge base ────────────────────────────────────────────────

export const setKnowledgeBaseInput = z.object({ id: uuid, enabled: z.boolean() });
export const setKnowledgeBaseOutput = z.object({ id: uuid, isKb: z.boolean(), kbStatus });

// ── folders ───────────────────────────────────────────────────────

export const createFolderInput = z.object({
  name: z.string().trim().min(1, "Folder name is required").max(120),
  parentId: uuid.nullable().default(null),
});
export const createFolderOutput = z.object({ id: uuid });

export const listFoldersInput = z.object({});
export const listFoldersOutput = z.object({
  items: z.array(
    z.object({
      id: uuid,
      parentId: uuid.nullable(),
      name: z.string(),
      createdAt: z.string().datetime(),
    }),
  ),
});

export const renameFolderInput = z.object({ id: uuid, name: z.string().trim().min(1).max(120) });
export const renameFolderOutput = z.object({ id: uuid });
